import { Skeleton } from '@/shared/components/ui/skeleton';
import { ErrorState } from '../../analytics/shared/ErrorState';
import { useReferralSubjectFeedback } from '../../../hooks/useReferralFeedbackData';

const STEP_LABELS: Record<string, string> = {
  cs_feedback_day7: 'Day-7 ask',
  cs_feedback_day14_reminder: 'Day-14 reminder',
  sp_followup_day30: 'Day-30 SP follow-up',
};

const CONVERTED_LABELS: Record<string, string> = {
  YES: 'Converted',
  NOT_YET: 'Not yet',
  NO: 'Did not convert',
};

const dash = '—';
const yesNo = (value: boolean | null): string => (value == null ? dash : value ? 'Yes' : 'No');
const ratingText = (value: number | null): string => (value != null ? `${value}/5` : dash);

const AnswerPair = ({ label, value }: { label: string; value: string }) => (
  <span className="text-[12px]" style={{ color: '#46584f' }}>
    <span className="font-bold uppercase text-[10.5px] tracking-[.3px]" style={{ color: '#7f8a83' }}>
      {label}
    </span>{' '}
    {value}
  </span>
);

const BlockTitle = ({ children }: { children: string }) => (
  <p className="text-[11px] font-bold uppercase tracking-[.3px] mb-1" style={{ color: '#1b2a23' }}>
    {children}
  </p>
);

const StatusChip = ({ status }: { status: string }) => (
  <span
    className="inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[.3px]"
    style={
      status === 'SENT'
        ? { color: '#2c6a4a', backgroundColor: '#e3ede7' }
        : status === 'FAILED'
          ? { color: '#a04545', backgroundColor: '#f3e2e2' }
          : { color: '#7f8a83', backgroundColor: '#f0ede6' }
    }
  >
    {status}
  </span>
);

type EngagementLogFeedbackPanelProps = {
  subjectType: string;
  subjectId: string;
};

/**
 * Feature 102 — inline feedback drill-down for one engagement-log row: the SP
 * follow-up answers, the care-seeker answers, and the follow-up email trail
 * for the row's source record. Mounted only on expand, so the query fires
 * lazily. Legacy rows with composite source ids miss cleanly (all-null
 * response) and land on the no-feedback state.
 */
export const EngagementLogFeedbackPanel = ({
  subjectType,
  subjectId,
}: EngagementLogFeedbackPanelProps) => {
  const { data, isLoading, isError, refetch } = useReferralSubjectFeedback(subjectType, subjectId);

  if (isError) {
    return (
      <ErrorState
        message="Failed to load feedback for this engagement. Please try again."
        onRetry={() => refetch()}
      />
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-2 py-2" data-testid="engagement-feedback-loading">
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-10 w-full rounded-lg" />
      </div>
    );
  }

  const { sp, cs, followups } = data;

  if (!sp && !cs && followups.length === 0) {
    return <p className="py-2 text-sm text-gray-500">No feedback yet for this engagement.</p>;
  }

  return (
    <div
      className="flex flex-wrap gap-x-10 gap-y-3 py-2"
      data-testid="engagement-feedback-panel"
    >
      {sp && (
        <div className="space-y-1">
          <BlockTitle>SP follow-up</BlockTitle>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <AnswerPair label="Reached" value={yesNo(sp.reached)} />
            <AnswerPair label="Rating" value={ratingText(sp.rating)} />
            <AnswerPair
              label="Converted"
              value={(sp.converted && CONVERTED_LABELS[sp.converted]) || dash}
            />
          </div>
          {sp.reason && (
            <p className="text-[12px] italic" style={{ color: '#46584f' }}>
              “{sp.reason}”
            </p>
          )}
        </div>
      )}
      {cs && (
        <div className="space-y-1">
          <BlockTitle>Care seeker</BlockTitle>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <AnswerPair label="Connected" value={yesNo(cs.reached)} />
            <AnswerPair label="Rating" value={ratingText(cs.rating)} />
          </div>
        </div>
      )}
      {followups.length > 0 && (
        <div className="space-y-1">
          <BlockTitle>Follow-up emails</BlockTitle>
          <ul className="space-y-1">
            {followups.map((f) => (
              <li key={f.stepKey} className="flex items-center gap-2">
                <span className="text-[12px]" style={{ color: '#46584f' }}>
                  {STEP_LABELS[f.stepKey] ?? f.stepKey}
                </span>
                <StatusChip status={f.status} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
